import { Sidebar, type SidebarSection } from '@zyxantech/tux'
import { LogOut, Moon, Sun, User } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { ROUTES } from '@/constants/routes'
import { useAlarmCount } from '@/hooks/useAlarmCount'
import { useAuth } from '@/hooks/useAuth'
import { useManifest } from '@/hooks/useManifest'
import { useTenant } from '@/hooks/useTenant'
import { useUiStore } from '@/store/ui.store'
import { NavIcon } from './navIcon'

/** Whether the current path belongs to a nav item's route subtree. */
function isRouteActive(pathname: string, route: string): boolean {
  return pathname === route || pathname.startsWith(`${route}/`)
}

/**
 * Authenticated navigation rail. Builds tux Sidebar sections from the manifest
 * (module + permission filtered), shows the live alarm badge, and renders a
 * brand header + user account footer. Navigation is SPA (onClick → navigate).
 */
export function AppSidebar() {
  const { navSections } = useManifest()
  const alarmCount = useAlarmCount()
  const collapsed = useUiStore((s) => s.sidebarCollapsed)
  const setCollapsed = useUiStore((s) => s.setSidebarCollapsed)
  const navigate = useNavigate()
  const location = useLocation()

  const sections: SidebarSection[] = navSections.map((section) => ({
    label: section.label,
    items: section.items.map((item) => ({
      id: item.id,
      label: item.label,
      icon: <NavIcon name={item.icon} />,
      active: isRouteActive(location.pathname, item.route),
      onClick: () => navigate(item.route),
      ...(item.id === 'alarms' && alarmCount > 0 ? { badge: alarmCount } : {}),
    })),
  }))

  return (
    <Sidebar
      sections={sections}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      footer={<SidebarFooter collapsed={collapsed} />}
    />
  )
}

/** Brand-coloured circular avatar; mirrors the demo's account avatar. */
function Avatar() {
  return (
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-accent bg-accent-dim">
      <User size={14} className="text-accent" />
    </div>
  )
}

/**
 * Sidebar footer: company label, an account button (avatar + name + role) that
 * opens a Sign-out menu, and a theme toggle — matching the demo's footer.
 */
function SidebarFooter({ collapsed }: { collapsed: boolean }) {
  const { user, logout } = useAuth()
  const { tenantName } = useTenant()
  const theme = useUiStore((s) => s.theme)
  const setTheme = useUiStore((s) => s.setTheme)
  const navigate = useNavigate()
  const isDark = theme === 'tap-dark' || theme === 'scada-dark'

  const [menuOpen, setMenuOpen] = useState(false)
  const accountRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close the menu on outside click.
  useEffect(() => {
    if (!menuOpen) return
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (accountRef.current?.contains(target) || menuRef.current?.contains(target)) return
      setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [menuOpen])

  const handleLogout = () => {
    setMenuOpen(false)
    void logout().then(() => navigate(ROUTES.LOGIN, { replace: true }))
  }

  return (
    <div className="px-2 pb-1">
      {!collapsed && tenantName && (
        <div className="truncate px-2 pb-1.5 text-[11px] text-text-muted">{tenantName}</div>
      )}

      {/* Account button → Sign-out menu */}
      <div ref={accountRef}>
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          title={collapsed ? 'Account' : undefined}
          className={`flex w-full items-center gap-2 rounded-md py-2 text-left hover:bg-bg-hover ${
            collapsed ? 'justify-center px-0' : 'px-2'
          } ${menuOpen ? 'bg-bg-hover' : ''}`}
        >
          <Avatar />
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-medium text-text-primary">
                {user?.name ?? 'User'}
              </div>
              <div className="truncate text-[11px] text-text-muted">{user?.role ?? ''}</div>
            </div>
          )}
          {!collapsed && <LogOut size={14} className="shrink-0 text-text-muted" />}
        </button>
      </div>

      {menuOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed bottom-16 left-3 z-50 min-w-[210px] overflow-hidden rounded-lg border border-border bg-bg-elevated shadow-2xl"
          >
            <div className="border-b border-border-subtle px-3.5 py-2.5">
              <div className="truncate text-[13px] font-semibold text-text-primary">
                {user?.name ?? 'User'}
              </div>
              <div className="truncate text-[11px] text-text-muted">{user?.email ?? ''}</div>
              {user?.role && <div className="mt-0.5 text-[10px] text-accent">{user.role}</div>}
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-[13px] text-danger hover:bg-bg-hover"
            >
              <LogOut size={14} />
              Sign out
            </button>
          </div>,
          document.body,
        )}

      {/* Theme toggle */}
      <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-start'}`}>
        <button
          type="button"
          onClick={() => setTheme(isDark ? 'tap-light' : 'tap-dark')}
          title={isDark ? 'Switch to light' : 'Switch to dark'}
          className="flex items-center rounded-md p-2 text-text-secondary hover:bg-bg-hover"
        >
          {isDark ? <Sun size={15} /> : <Moon size={15} />}
        </button>
      </div>
    </div>
  )
}
